import { createFileRoute } from "@tanstack/react-router";
import { localProductionSection } from "@/lib/seo/local-production";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, localBusinessForCity, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad kostar animation i Göteborg?",
    a: "Motion graphics 15–30 sekunder kostar 25 000–60 000 kr. Animerad förklaringsfilm 60–90 sekunder 60 000–150 000 kr. 3D-animation från 120 000 kr. Löpande animationsflöde via abonnemang från 14 995 kr/mån.",
  },
  {
    q: "Hur snabbt kan ni leverera?",
    a: "Motion graphics 2–3 veckor från godkänt manus, förklaringsfilm 3–5 veckor och 3D-animation 5–8 veckor beroende på modelleringsbehov.",
  },
  {
    q: "Jobbar ni med industri- och teknikbolag i Västsverige?",
    a: "Ja. Animation är särskilt vanligt hos tillverkande industri och teknikbolag i Göteborgsregionen, där produkten ofta är för stor, för liten eller för dold inuti en maskin för att filmas.",
  },
  {
    q: "Kan ni kombinera animation med filmat material?",
    a: "Ja. En vanlig lösning är filmad basfilm med animerade förklaringssekvenser och grafik ovanpå — det ger både trovärdighet och tydlighet i samma produktion.",
  },
  {
    q: "Behöver ni vår grafiska manual?",
    a: "Helst. Med brand book blir animationen konsekvent med er övriga kommunikation. Saknas manual tar vi fram ett enkelt visuellt ramverk som del av storyboardsteget.",
  },
];

export const Route = createFileRoute("/animation_/goteborg")({
  head: () => ({
    meta: [
      { title: "Animation i Göteborg — se bildmanuset innan vi animerar" },
      {
        name: "description",
        content:
          "Motion graphics, förklaringsfilm och 3D för västsvensk industri och tech. Se exempel från Göteborg, godkänn bildmanus innan vi animerar — klart på 2–5 veckor.",
      },
      { property: "og:title", content: "Animation i Göteborg — se bildmanuset innan vi animerar" },
      {
        property: "og:description",
        content: "Animerad förklaringsfilm och motion graphics i Göteborg — se pris och exempel.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/animation/goteborg" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/animation/goteborg" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/animation/goteborg", name: "Animeringar i Göteborg — animationsbyrå för industri och tech", description: "Animeringar i Göteborg: motion graphics, animerad förklaringsfilm och 3D för industri och tech. Bildmanus godkänns innan animation, leverans på 2–5 veckor."})) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          localBusinessForCity({
            city: "Göteborg",
            path: "/animation/goteborg",
            description:
              "Animation och motion graphics i Göteborg — förklaringsfilm, 3D och animerad reklamfilm.",
            postalRegion: "Västra Götalands län",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Animation", path: "/motion-graphics" },
            { name: "Göteborg", path: "/animation/goteborg" },
          ]),
        ),
      },
    ],
  }),
  component: AnimationGoteborgPage,
});

function AnimationGoteborgPage() {
  return (
    <LandingPage
      eyebrow="Animation Göteborg"
      title={<>Animation i Göteborg — visa det kameran inte kommer åt.</>}
      lede="Motion graphics, animerad förklaringsfilm och 3D för industri-, teknik- och tjänstebolag i Västsverige. Vi gör komplexa produkter begripliga på under 90 sekunder — med storyboard och fast pris innan produktion."
      tldr={
        <p>
          Animation i Göteborg kostar typiskt <strong>25 000–150 000 kr</strong>{" "}
          och levereras på <strong>2–5 veckor</strong>. Fenny producerar motion
          graphics, förklaringsfilm och 3D-animation för bolag i
          Göteborgsregionen — ofta för produkter som är omöjliga att filma.
        </p>
      }
      ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
      ctaSecondary={{ label: "Se priser", to: "/priser" }}
      trackingId="animation-goteborg"
      bullets={[
        { title: "Industri och teknik", body: "Vana vid CAD-underlag, processflöden och tekniska förklaringar." },
        { title: "Storyboard innan produktion", body: "Ni godkänner bildmanus och tonalitet innan animationen börjar." },
        { title: "Alla format", body: "16:9, 9:16 och 1:1 med undertexter för mässa, sajt och LinkedIn." },
      ]}
      pricing={{
        heading: "Vad kostar animation i Göteborg?",
        intro: "Riktpriser exklusive moms.",
        rows: [
          { format: "Motion graphics 15–30 sek", scope: "Manus, design, animation, ljud", price: "25 000–60 000 kr", lead: "2–3 veckor" },
          { format: "Förklaringsfilm 60–90 sek", scope: "Storyboard, illustration, speaker", price: "60 000–150 000 kr", lead: "3–5 veckor" },
          { format: "3D-animation", scope: "Modellering, rendering, komposition", price: "från 120 000 kr", lead: "5–8 veckor" },
          { format: "Löpande animation", scope: "Abonnemang med kontinuerlig output", price: "från 14 995 kr/mån", lead: "10–14 dagar till första klipp" },
        ],
      }}
      sections={[
        localProductionSection("goteborg", "Göteborg"),
        {
          id: "vanliga-uppdrag",
          heading: "Vanliga animationsuppdrag i Västsverige",
          body: (
            <>
              <p>
                Produktförklaring för tillverkande industri, processfilm för
                fordons- och energisektorn, onboarding och säkerhetsinstruktioner
                samt mässmaterial som ska fungera utan ljud.
              </p>
              <p>
                Behöver ni filmad produktion istället, se{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/goteborg">
                  filmproduktion i Göteborg
                </a>
                .
              </p>
            </>
          ),
        },
        {
          id: "tekniker",
          heading: "Tekniker vi arbetar i",
          body: (
            <p>
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/motion-graphics">Motion graphics</a>,{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/animerad-forklaringsfilm">animerad förklaringsfilm</a>,{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/animerad-reklamfilm">animerad reklamfilm</a> och{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/3d-animation">3D-animation</a>.
            </p>
          ),
        },
      ]}
      faq={FAQ}
      caseLimit={4}
      riskReducers
      closing={{
        title: "Ska ni göra animation i Göteborg?",
        body: "Boka 30 minuter så går vi igenom format, längd och prisnivå för ert case.",
        primary: { label: "Boka strategimöte", to: "/kontakt" },
        secondary: { label: "Se case", to: "/case" },
      }}
    />
  );
}
